import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Upload, Search, Pencil, Trash2, Users, Building2, User, UserX, Plane, CheckSquare, GraduationCap, Wand2, ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactTypeConfig = {
  empresa: { label: 'Empresa', icon: Building2, className: 'bg-purple-100 text-purple-700' },
  asociacion: { label: 'Asociación', icon: Users, className: 'bg-green-100 text-green-700' },
  particular: { label: 'Particular', icon: User, className: 'bg-blue-100 text-blue-700' },
  agencia_de_viajes: { label: 'Agencia de Viajes', icon: Plane, className: 'bg-orange-100 text-orange-700' },
  colegio: { label: 'Colegio', icon: GraduationCap, className: 'bg-teal-100 text-teal-700' }
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exclusions, setExclusions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [retagLoading, setRetagLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTypeValue, setBulkTypeValue] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 1000;

  const [formData, setFormData] = useState({ name: '', email: '', type: 'empresa', company: '', notes: '' });
  const [editFormData, setEditFormData] = useState({ name: '', email: '', type: 'empresa', company: '', notes: '' });

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Error al cargar los contactos');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExclusions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/exclusions`);
      setExclusions(response.data);
    } catch (error) {
      toast.error('Error al cargar la lista de exclusión');
    }
  }, []);

  useEffect(() => { fetchContacts(); fetchExclusions(); }, [fetchContacts, fetchExclusions]);

  useEffect(() => {
    let filtered = contacts;
    if (filterType !== 'all') filtered = filtered.filter(c => c.type === filterType);
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.company && c.company.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    // Sort
    filtered = [...filtered].sort((a, b) => {
      let valA, valB;
      if (sortField === 'created_at') {
        valA = a.created_at || '';
        valB = b.created_at || '';
      } else if (sortField === 'type') {
        valA = (contactTypeConfig[a.type]?.label || a.type || '').toLowerCase();
        valB = (contactTypeConfig[b.type]?.label || b.type || '').toLowerCase();
      } else {
        valA = (a[sortField] || '').toLowerCase();
        valB = (b[sortField] || '').toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredContacts(filtered);
  }, [contacts, searchTerm, filterType, sortField, sortDir]);

  // Clear selection when filter changes
  useEffect(() => { setSelectedIds(new Set()); }, [filterType, searchTerm]);

  // Reset to first page when filters/search/sort change
  useEffect(() => { setCurrentPage(1); }, [filterType, searchTerm, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedContacts = filteredContacts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contacts`, formData);
      toast.success('Contacto agregado exitosamente');
      setShowAddDialog(false);
      setFormData({ name: '', email: '', type: 'empresa', company: '', notes: '' });
      fetchContacts();
    } catch (error) {
      toast.error('Error al agregar el contacto');
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este contacto?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contacto eliminado');
      fetchContacts();
    } catch (error) {
      toast.error('Error al eliminar el contacto');
    }
  };

  const handleExcludeContact = async (contact) => {
    if (!window.confirm(`¿Excluir a ${contact.email} de futuras campañas?`)) return;
    try {
      await axios.post(`${API}/exclusions`, {
        email: contact.email,
        reason: `Excluido desde contactos: ${contact.name || contact.email}`
      });
      toast.success('Contacto añadido a la lista de exclusión');
      fetchExclusions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al excluir el contacto');
    }
  };

  const handleUnexcludeContact = async (contact) => {
    const exclusionEntry = exclusionMap.get((contact.email || '').toLowerCase());
    if (!exclusionEntry) return;
    if (!window.confirm(`¿Quitar a ${contact.email} de la lista de exclusión?`)) return;
    try {
      await axios.delete(`${API}/exclusions/${exclusionEntry.id}`);
      toast.success('Contacto removido de la lista de exclusión');
      fetchExclusions();
    } catch (error) {
      toast.error('Error al quitar de la exclusión');
    }
  };

  const handleOpenEdit = (contact) => {
    setEditingContactId(contact.id);
    setEditFormData({
      name: contact.name || '', email: contact.email || '',
      type: contact.type || 'empresa', company: contact.company || '', notes: contact.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleCloseEdit = () => {
    setShowEditDialog(false);
    setEditingContactId(null);
    setEditFormData({ name: '', email: '', type: 'empresa', company: '', notes: '' });
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!editingContactId) return;
    try {
      setEditLoading(true);
      await axios.put(`${API}/contacts/${editingContactId}`, editFormData);
      toast.success('Contacto actualizado correctamente');
      handleCloseEdit();
      fetchContacts();
    } catch (error) {
      toast.error('Error al actualizar el contacto');
    } finally {
      setEditLoading(false);
    }
  };

  const handleFileChange = (e) => setCsvFile(e.target.files[0]);

  const handleImportCSV = async () => {
    if (!csvFile) { toast.error('Por favor selecciona un archivo'); return; }
    setImportLoading(true);
    const fd = new FormData();
    fd.append('file', csvFile);
    try {
      const response = await axios.post(`${API}/contacts/bulk-import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Importados: ${response.data.success} contactos. Fallidos: ${response.data.failed}`);
      setShowImportDialog(false);
      setCsvFile(null);
      fetchContacts();
    } catch (error) {
      toast.error('Error al importar contactos');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csv = 'name,email,type,company,notes\nJuan Pérez,juan@empresa.com,empresa,Empresa ABC,Cliente importante\nMaría García,maria@asociacion.org,asociacion,Asociación XYZ,Contacto anual\nLuis López,luis@correo.com,particular,,Cliente directo\nViajes Sol,info@viajessol.com,agencia_de_viajes,Viajes Sol S.L.,Agencia de viajes';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ejemplo_contactos.csv';
    a.click();
  };

  const handleRetagAgencies = async () => {
    if (!window.confirm('¿Clasificar automáticamente todos los contactos por tipo?')) return;
    setRetagLoading(true);
    try {
      const response = await axios.post(`${API}/contacts/auto-classify`);
      const { modified_count, details } = response.data;
      if (modified_count > 0) {
        toast.success(response.data.message);
        fetchContacts();
      } else {
        toast.info('No se encontraron contactos nuevos para clasificar');
      }
    } catch (error) {
      toast.error('Error al clasificar contactos');
    } finally {
      setRetagLoading(false);
    }
  };

  // ===== BULK ACTIONS =====
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts.map(c => c.id)));
    }
  };

  const handleBulkChangeType = async () => {
    if (!bulkTypeValue) { toast.error('Selecciona un tipo'); return; }
    const count = selectedIds.size;
    const label = contactTypeConfig[bulkTypeValue]?.label || bulkTypeValue;
    if (!window.confirm(`¿Cambiar ${count} contactos a tipo "${label}"?`)) return;
    setBulkLoading(true);
    try {
      const response = await axios.post(`${API}/contacts/bulk-update-type`, {
        contact_ids: Array.from(selectedIds),
        type: bulkTypeValue
      });
      toast.success(`${response.data.modified_count} contactos actualizados a "${label}"`);
      setSelectedIds(new Set());
      setBulkTypeValue('');
      fetchContacts();
    } catch (error) {
      toast.error('Error al cambiar el tipo');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!window.confirm(`¿Eliminar ${count} contactos? Esta acción no se puede deshacer.`)) return;
    setBulkLoading(true);
    try {
      const response = await axios.post(`${API}/contacts/bulk-delete`, {
        contact_ids: Array.from(selectedIds)
      });
      toast.success(`${response.data.deleted_count} contactos eliminados`);
      setSelectedIds(new Set());
      fetchContacts();
    } catch (error) {
      toast.error('Error al eliminar contactos');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkExclude = async () => {
    const count = selectedIds.size;
    if (!window.confirm(`¿Excluir ${count} contactos de futuras campañas?`)) return;
    setBulkLoading(true);
    try {
      const response = await axios.post(`${API}/contacts/bulk-exclude`, {
        contact_ids: Array.from(selectedIds)
      });
      toast.success(`${response.data.excluded_count} contactos añadidos a la lista de exclusión`);
      setSelectedIds(new Set());
      fetchExclusions();
    } catch (error) {
      toast.error('Error al excluir contactos');
    } finally {
      setBulkLoading(false);
    }
  };

  const exclusionMap = useMemo(() => new Map(exclusions.map((entry) => [entry.email.toLowerCase(), entry])), [exclusions]);
  const excludedEmails = useMemo(() => new Set(exclusionMap.keys()), [exclusionMap]);
  const allSelected = filteredContacts.length > 0 && selectedIds.size === filteredContacts.length;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contactos</h1>
          <p className="mt-2 text-gray-600">Gestiona tu base de datos de clientes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRetagAgencies} disabled={retagLoading} data-testid="auto-classify-button">
            <Wand2 className="h-4 w-4 mr-2" />
            {retagLoading ? 'Clasificando...' : 'Clasificar'}
          </Button>
          <Button variant="outline" onClick={() => window.open(`${API}/contacts/export`, '_blank')} data-testid="export-contacts-button">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-csv-button">
                <Upload className="h-4 w-4 mr-2" />
                Importar CSV
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="import-contacts-dialog">
              <DialogHeader>
                <DialogTitle>Importar Contactos</DialogTitle>
                <DialogDescription>
                  Sube un archivo Excel (.xlsx, .xls) o CSV con tus contactos. El archivo debe tener las columnas: name, email, type, company, notes
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="csv-file">Archivo Excel o CSV</Label>
                  <Input id="csv-file" type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="mt-2" data-testid="import-file-input" />
                </div>
                <Button variant="outline" onClick={downloadSampleCSV} className="w-full" data-testid="download-sample-csv-button">
                  Descargar CSV de Ejemplo
                </Button>
                <div className="flex gap-2">
                  <Button onClick={handleImportCSV} disabled={importLoading} className="flex-1" data-testid="confirm-import-button">
                    {importLoading ? 'Importando...' : 'Importar'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowImportDialog(false)} data-testid="cancel-import-button">Cancelar</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-button">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Contacto
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-contact-dialog">
              <DialogHeader>
                <DialogTitle>Agregar Nuevo Contacto</DialogTitle>
                <DialogDescription>Completa la información del contacto</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddContact} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="add-contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="add-contact-email-input" />
                </div>
                <div>
                  <Label htmlFor="type">Tipo *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger data-testid="add-contact-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="empresa">Empresa</SelectItem>
                      <SelectItem value="asociacion">Asociación</SelectItem>
                      <SelectItem value="particular">Particular</SelectItem>
                      <SelectItem value="agencia_de_viajes">Agencia de Viajes</SelectItem>
                      <SelectItem value="colegio">Colegio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="company">Empresa/Organización</Label>
                  <Input id="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} data-testid="add-contact-company-input" />
                </div>
                <div>
                  <Label htmlFor="notes">Notas</Label>
                  <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="add-contact-notes-input" />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" data-testid="confirm-add-contact-button">Agregar</Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} data-testid="cancel-add-contact-button">Cancelar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showEditDialog} onOpenChange={(open) => (open ? setShowEditDialog(true) : handleCloseEdit())}>
            <DialogContent data-testid="edit-contact-dialog">
              <DialogHeader>
                <DialogTitle>Editar Contacto</DialogTitle>
                <DialogDescription>Actualiza los datos del contacto</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateContact} className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Nombre *</Label>
                  <Input id="edit-name" value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} required data-testid="edit-contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="edit-email">Email *</Label>
                  <Input id="edit-email" type="email" value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} required data-testid="edit-contact-email-input" />
                </div>
                <div>
                  <Label htmlFor="edit-type">Tipo *</Label>
                  <Select value={editFormData.type} onValueChange={(value) => setEditFormData({ ...editFormData, type: value })}>
                    <SelectTrigger data-testid="edit-contact-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="empresa">Empresa</SelectItem>
                      <SelectItem value="asociacion">Asociación</SelectItem>
                      <SelectItem value="particular">Particular</SelectItem>
                      <SelectItem value="agencia_de_viajes">Agencia de Viajes</SelectItem>
                      <SelectItem value="colegio">Colegio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-company">Empresa/Organización</Label>
                  <Input id="edit-company" value={editFormData.company} onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })} data-testid="edit-contact-company-input" />
                </div>
                <div>
                  <Label htmlFor="edit-notes">Notas</Label>
                  <Textarea id="edit-notes" value={editFormData.notes} onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })} rows={3} data-testid="edit-contact-notes-input" />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={editLoading} data-testid="confirm-edit-contact-button">
                    {editLoading ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseEdit} data-testid="cancel-edit-contact-button">Cancelar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="bulk-actions-bar">
          <CheckSquare className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-900" data-testid="bulk-selected-count">
            {selectedIds.size} seleccionado{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="h-5 w-px bg-blue-300" />
          <div className="flex items-center gap-2">
            <Select value={bulkTypeValue} onValueChange={setBulkTypeValue}>
              <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="bulk-type-select">
                <SelectValue placeholder="Cambiar tipo a..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="empresa">Empresa</SelectItem>
                <SelectItem value="asociacion">Asociación</SelectItem>
                <SelectItem value="particular">Particular</SelectItem>
                <SelectItem value="agencia_de_viajes">Agencia de Viajes</SelectItem>
                <SelectItem value="colegio">Colegio</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleBulkChangeType} disabled={bulkLoading || !bulkTypeValue} data-testid="bulk-change-type-button">
              Aplicar
            </Button>
          </div>
          <div className="h-5 w-px bg-blue-300" />
          <Button size="sm" variant="outline" onClick={handleBulkExclude} disabled={bulkLoading} data-testid="bulk-exclude-button">
            <UserX className="h-4 w-4 mr-1" />
            Excluir
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={handleBulkDelete} disabled={bulkLoading} data-testid="bulk-delete-button">
            <Trash2 className="h-4 w-4 mr-1" />
            Eliminar
          </Button>
          <div className="ml-auto">
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-selection">
              Limpiar selección
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Lista de Contactos ({filteredContacts.length})</CardTitle>
          <CardDescription>{contacts.length} contactos totales</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input placeholder="Buscar por nombre, email o empresa..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-contacts-input" />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-contacts-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="empresa">Empresas</SelectItem>
                <SelectItem value="asociacion">Asociaciones</SelectItem>
                <SelectItem value="particular">Particulares</SelectItem>
                <SelectItem value="agencia_de_viajes">Agencias de Viajes</SelectItem>
                <SelectItem value="colegio">Colegios</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando contactos...</div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No se encontraron contactos. Agrega o importa contactos para comenzar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('name')} data-testid="sort-name">
                      <span className="flex items-center">Nombre <SortIcon field="name" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('email')} data-testid="sort-email">
                      <span className="flex items-center">Email <SortIcon field="email" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('type')} data-testid="sort-type">
                      <span className="flex items-center">Tipo <SortIcon field="type" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('company')} data-testid="sort-company">
                      <span className="flex items-center">Empresa <SortIcon field="company" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('created_at')} data-testid="sort-date">
                      <span className="flex items-center">Fecha <SortIcon field="created_at" /></span>
                    </TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedContacts.map((contact) => {
                    const typeMeta = contactTypeConfig[contact.type] || contactTypeConfig.empresa;
                    const TypeIcon = typeMeta.icon;
                    const isExcluded = excludedEmails.has((contact.email || '').toLowerCase());
                    const isSelected = selectedIds.has(contact.id);
                    return (
                      <TableRow key={contact.id} className={isSelected ? 'bg-blue-50' : ''} data-testid={`contact-row-${contact.id}`}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(contact.id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            data-testid={`select-contact-${contact.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium" data-testid={`contact-name-${contact.id}`}>
                          <div className="flex items-center gap-2">
                            <span>{contact.name}</span>
                            {isExcluded && (
                              <button type="button" onClick={() => handleUnexcludeContact(contact)} className="cursor-pointer" data-testid={`contact-excluded-${contact.id}`}>
                                <Badge className="bg-red-100 text-red-700">Excluido</Badge>
                              </button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`contact-email-${contact.id}`}>{contact.email}</TableCell>
                        <TableCell data-testid={`contact-type-${contact.id}`}>
                          <Badge variant="secondary" className={typeMeta.className}>
                            <TypeIcon className="h-3 w-3 mr-1" /> {typeMeta.label}
                          </Badge>
                        </TableCell>
                        <TableCell data-testid={`contact-company-${contact.id}`}>{contact.company || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-500" data-testid={`contact-date-${contact.id}`}>{formatDate(contact.created_at)}</TableCell>
                        <TableCell className="text-right" data-testid={`contact-actions-${contact.id}`}>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(contact)} data-testid={`edit-contact-${contact.id}`}>
                            <Pencil className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleExcludeContact(contact)} data-testid={`exclude-contact-${contact.id}`}>
                            <UserX className="h-4 w-4 text-orange-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteContact(contact.id)} data-testid={`delete-contact-${contact.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t" data-testid="contacts-pagination">
                  <div className="text-sm text-gray-600">
                    Mostrando <span className="font-semibold">{(safePage - 1) * PAGE_SIZE + 1}</span>
                    {' - '}
                    <span className="font-semibold">{Math.min(safePage * PAGE_SIZE, filteredContacts.length)}</span>
                    {' de '}
                    <span className="font-semibold">{filteredContacts.length}</span>
                    {' contactos'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={safePage === 1}
                      data-testid="pagination-first"
                    >
                      «
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      data-testid="pagination-prev"
                    >
                      Anterior
                    </Button>
                    <span className="text-sm text-gray-700 px-2" data-testid="pagination-info">
                      Página <span className="font-semibold">{safePage}</span> de <span className="font-semibold">{totalPages}</span>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      data-testid="pagination-next"
                    >
                      Siguiente
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={safePage === totalPages}
                      data-testid="pagination-last"
                    >
                      »
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Contacts;
